import {
	Box,
	Center,
	createStyles,
	Group,
	Loader,
	Paper,
	ScrollArea,
	SimpleGrid,
	Stack,
} from '@mantine/core';
import type { PolicyTemplate, PolicyTemplateOut } from '@repo/api-codegen';
import {
	useApiGetPolicyTemplates,
	useApiListPolicyTemplates,
} from '@repo/api-codegen';
import { Badge, Icon, Text } from '@repo/foundations';
import { useLocation, useNavigate } from 'react-router';
import { useAuthUser } from '../../../api';
import { useSpecCardStyles } from '../../../components/IntegrationSpecCard/IntegrationSpecCard.helpers';
import { closeAllModals } from '../../../components/ModalManager';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { getPolicyPiiScanBannerInformation } from '../../PoliciesCreatePage/utils/policyPiiScanBanner';
import { getIconName } from '../utils';

interface PolicyTemplateCardProps {
	template: PolicyTemplate;
}

const useStyles = createStyles((theme) => ({
	widgetItem: {
		cursor: 'pointer',
		'&:hover': {
			background: theme.other.getColor('surface/primary/hover'),
		},
	},
}));

function PolicyTemplateCard({ template }: PolicyTemplateCardProps) {
	const { classes } = useStyles();
	const { user } = useAuthUser();
	const navigate = useNavigate();
	const location = useLocation();

	const handleClick = () => {
		closeAllModals();

		navigate('/policies/new', {
			state: {
				prev_url_path: location.pathname,
				policyTemplate: {
					...template,
				},
				owners: [user.id],
				subscribers: [user.id],
			},
		});
	};

	return (
		<Paper
			p="md"
			m="sm"
			radius="md"
			withBorder
			onClick={handleClick}
			className={classes.widgetItem}
			mx={0}
		>
			<Stack spacing="xs">
				<Text size="sm" weight="bold">
					{template.name}
				</Text>
				<Text size="sm">{template.description}</Text>
			</Stack>
		</Paper>
	);
}

function PolicyDataScanningTemplateCard({
	template,
}: {
	template: PolicyTemplateOut;
}) {
	const { classes } = useStyles();
	const { classes: cardClasses } = useSpecCardStyles();
	const { user } = useAuthUser();
	const navigate = useNavigate();
	const location = useLocation();
	const iconName = getIconName(template.icon);
	const { title, classifiers } = getPolicyPiiScanBannerInformation(
		template.title
	);

	const handleClick = () => {
		closeAllModals();

		navigate('/policies/new', {
			state: {
				prev_url_path: location.pathname,
				policyTemplate: {
					description: template.description,
					name: title,
					frameworks: template.frameworks,
					resource_rule: {
						operator: 'and',
						operands: [],
					},
					policy_rule: {
						operator: 'and',
						operands: [],
					},
					remediation: '',
					rule_title: '',
				},
				owners: [user.id],
				subscribers: [user.id],
				piiScanMetadata: {
					templateTitle: template.title,
					templatePiiClassifiers: classifiers,
				},
			},
		});
	};

	return (
		<Paper
			p="md"
			radius="md"
			withBorder
			onClick={handleClick}
			className={classes.widgetItem}
			w="fit-content"
		>
			<Stack spacing="sm">
				<Box className={cardClasses.logoWrapper}>
					<Icon name={iconName} />
				</Box>
				<Group spacing={0}>
					<Text size="sm">{title}</Text>
					<Text size="sm" color="text/secondary/default">
						{template.description}
					</Text>
				</Group>
				<Group spacing="2xs">
					{template.frameworks.map((framework) => (
						<Badge key={framework} w="fit-content">
							{framework}
						</Badge>
					))}
				</Group>
			</Stack>
		</Paper>
	);
}

function PolicyTemplates() {
	const { theme } = useStyles();
	const { data: templates, isLoading } = useApiListPolicyTemplates({});
	const { dataScanning } = useFeatureFlags();
	const {
		data: dataScanningTemplates,
		isLoading: isLoadingDataScanningTemplates,
	} = useApiGetPolicyTemplates({});

	if (isLoading || isLoadingDataScanningTemplates) {
		return (
			<Center h={theme.other.space[120]}>
				<Loader />
			</Center>
		);
	}

	if (dataScanning) {
		return (
			<SimpleGrid cols={3} spacing="md">
				{dataScanningTemplates?.map((template) => (
					<PolicyDataScanningTemplateCard
						key={template.title}
						template={template}
					/>
				))}
			</SimpleGrid>
		);
	}
	return (
		<ScrollArea h={theme.other.space[120]}>
			{templates?.map((template) => (
				<PolicyTemplateCard key={template.name} template={template} />
			))}
		</ScrollArea>
	);
}

export default PolicyTemplates;
